import { cva } from "class-variance-authority";

export const buttonVariants = cva(
  // structural
  "inline-flex items-center justify-center gap-2 shrink-0 whitespace-nowrap rounded-[calc(var(--radius)-2px)] " +
    // every variant reserves a 1px border box (transparent by default) so switching to `outline`
    // — which only sets the border color — never shifts size or layout.
    "border border-transparent " +
    "text-sm font-medium transition-all outline-none " +
    // stateful
    "disabled:pointer-events-none disabled:opacity-50 " +
    "focus-visible:ring-ring/50 focus-visible:ring-[3px] " +
    "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 " +
    // svg children — structural plain, size guarded so a consumer's own size-* on the icon wins
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 " +
    // while loading, hide every child svg so only the leading Spinner (a div, not an svg) shows.
    // Single arbitrary selector (not stacked data-*:[&_svg]:) so Tailwind v3 reliably emits it.
    "[&[data-loading=true]_svg]:hidden",
  {
    variants: {
      variant: {
        solid: "shadow-xs",
        outline: "border-input bg-background shadow-xs",
        ghost: "",
        link: "underline-offset-4 hover:underline",
      },
      color: {
        primary: "",
        secondary: "",
        destructive: "",
      },
      size: {
        sm: "h-8 gap-1.5 px-2",
        md: "h-9 px-3",
        lg: "h-10 px-4",
      },
      iconOnly: {
        true: "p-0",
        false: "",
      },
    },
    compoundVariants: [
      // solid — filled background, on-color foreground
      { variant: "solid", color: "primary", class: "bg-primary text-primary-foreground hover:bg-primary/90" },
      {
        variant: "solid",
        color: "secondary",
        class: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      },
      {
        variant: "solid",
        color: "destructive",
        class:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
      },
      // outline — colored border + text, tinted hover
      { variant: "outline", color: "primary", class: "hover:bg-accent hover:text-accent-foreground" },
      { variant: "outline", color: "secondary", class: "hover:bg-accent hover:text-accent-foreground" },
      {
        variant: "outline",
        color: "destructive",
        class: "border-destructive/40 text-destructive hover:bg-destructive/10",
      },
      // ghost — no background until hover
      { variant: "ghost", color: "primary", class: "hover:bg-accent hover:text-accent-foreground" },
      { variant: "ghost", color: "secondary", class: "hover:bg-accent hover:text-accent-foreground" },
      { variant: "ghost", color: "destructive", class: "text-destructive hover:bg-destructive/10" },
      // link — text-only, colored
      { variant: "link", color: "primary", class: "text-primary" },
      { variant: "link", color: "secondary", class: "text-secondary-foreground" },
      { variant: "link", color: "destructive", class: "text-destructive" },
      // iconOnly — square box per size (overrides the px/gap from size)
      { iconOnly: true, size: "sm", class: "size-7" },
      { iconOnly: true, size: "md", class: "size-9" },
      { iconOnly: true, size: "lg", class: "size-10" },
    ],
    defaultVariants: {
      variant: "solid",
      color: "primary",
      size: "md",
      iconOnly: false,
    },
  }
);

export const buttonGroupVariants = cva("inline-flex items-center", {
  variants: {
    layout: {
      // seamless — collapse the touching inner corners and borders into one continuous control
      joined:
        "[&>*:not(:last-child)]:border-r-0 [&>*:first-child]:!rounded-r-none [&>*:last-child]:!rounded-l-none " +
        "[&>*:not(:first-child):not(:last-child)]:!rounded-none",
      separated: "gap-2",
    },
  },
  defaultVariants: {
    layout: "joined",
  },
});
